import type { AuthorizationFailureReasons } from "./api/types.ts";
import { redirectOnForbidden } from "@sg-protect/core";
import { type PropsWithChildren, useLayoutEffect, useState } from "react";
import { type MiddlewareClient, withShellClient } from "./WorkspaceBoundary.tsx";

export interface ApiAccessRedirect {
    reason: AuthorizationFailureReasons;
    // A string, or a function resolved when the redirect fires (never during render), so callers can
    // defer to live state without re-installing the middleware.
    to: string | (() => string);
}

interface ApiAccessBoundaryProps extends PropsWithChildren {
    redirects: ApiAccessRedirect[];
    /** The module’s API client(s). Pass a memoized array when passing more than one. */
    client: MiddlewareClient | MiddlewareClient[];
}

export function ApiAccessBoundary({ children, redirects, client }: ApiAccessBoundaryProps) {
    const [isMiddlewareInstalled, setIsMiddlewareInstalled] = useState(false);

    useLayoutEffect(() => {
        const middlewares = redirects.map(({ reason, to }) =>
            redirectOnForbidden<AuthorizationFailureReasons>({ expectedFailureReason: reason, to })
        );

        const clients = withShellClient(client);

        clients.forEach(c => middlewares.forEach(middleware => c.use(middleware)));
        setIsMiddlewareInstalled(true);

        return () => {
            clients.forEach(c => middlewares.forEach(middleware => c.eject(middleware)));
        };
    }, [redirects, client]);

    if (!isMiddlewareInstalled) {
        return null;
    }

    return children;
}
